

import { Accessory, AdjacentEntity, Configuration, Connector, Direction, Entity, Material, Module, ModuleSummaryAccessory, ModuleSummaryCustomOption, ModuleSummaryElement, PriceOption, Product, ProductCategory, Series, SeriesData, YUIContext } from "./types/type_def";
import { MaterialLibrary, materialLibrary, ProductLibrary, productLibrary, seriesLibrary } from "./libraries";
import { AppSettings } from "./settings";
import { DragDropHandler } from "./handlers";
import Hammer from "hammerjs";

function isTouchDevice() {
  return 'ontouchstart' in document.documentElement;
}

/**
 * Math Helper
 * */
export class MathHelper {

    static normalizePi(angle: number) {
        var pi = Math.PI;
        while (angle > pi)
            angle -= 2 * pi;
        while (angle < -pi)
            angle += 2 * pi;
        return angle;
    }

    static mulberry32(a) {
        return function () {
            var t = a += 0x6D2B79F5;
            t = Math.imul(t ^ t >>> 15, t | 1);
            t ^= t + Math.imul(t ^ t >>> 7, t | 61);
            return ((t ^ t >>> 14) >>> 0) / 4294967296;
        }
    }

    static normalizedVector(vector: BABYLON.Vector3): BABYLON.Vector3 {
        var normalized = vector.normalize();
        var normalizedUnitLength = new BABYLON.Vector3(Math.ceil(normalized.x), Math.ceil(normalized.y), Math.ceil(normalized.z));
        return normalizedUnitLength;
    }

    static clamp = function (num, min, max) { return Math.min(Math.max(num, min), max); }


    static getRotationVector(baseAngle: number, additionAngle: number, axis: BABYLON.Axis): BABYLON.Vector3 {

        if (baseAngle === 0) {
            return BABYLON.Vector3.Zero();
        }

        var finalAngle = baseAngle + additionAngle;

        switch (axis) {
            case BABYLON.Axis.X: {
                return new BABYLON.Vector3(finalAngle, 0, 0);
            }
            case BABYLON.Axis.Y: {
                return new BABYLON.Vector3(0, finalAngle, 0);
            }
            case BABYLON.Axis.Z: {
                return new BABYLON.Vector3(0, 0, finalAngle);
            }
        }
    }

    static getUniqueValues(array: Array<any>) {
        var unique = [];
        var distinct = [];
        for (let i = 0; i < array.length; i++) {
            if (!unique[array[i]]) {
                distinct.push(array[i]);
                unique[array[i]] = 1;
            }
        }
        return distinct;
    }

    static getCircleXYZ(angleRadians: number, radius: number, center: BABYLON.Vector3) {
        return new BABYLON.Vector3((Math.cos(angleRadians) * radius) + center.x, 0, (Math.sin(angleRadians) * radius) + center.z)
    }
}

/**
 * Cryptography Helper
 * */

export class CryptographyHelper {
    static parseJwt(token: string) {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    };
}

/**
 * Notification Helper 
 */

export enum Severity {
    SUCCESS = "is-success",
    WARNING = "is-warning",
    ERROR = "is-error"
}

export class NotificationHelper {

    static showNotification(message: string, severity: Severity) {




    }
}

/**
 * Material Helper
 * */

export class MaterialHelper {

    static copyValues(src: BABYLON.Material, dest: BABYLON.Material) {
        var p = ["albedoColor", "alpha", "alphaCutOff", "alphaMode", "ambientColor", "backFaceCulling", "clearCoat",
            "directIntensity", "emissiveColor", "emissiveIntensity", "emissiveTexture", "albedoTexture", "bumpTexture", "environmentIntensity", "fillMode",
            "indexOfRefraction", "metallic", "microSurface", "metallicTexture", "microSurfaceTexture", "reflectionColor",
            "reflectionTexture", "reflectivityColor", "reflectivityTexture", "refractionTexture", "roughness", "sheen",
            "sideOrientation", "specularIntensity", "subSurface", "transparencyMode", "twoSidedLighting", "environmentBRDFTexture"];

        for (var i = 0; i < p.length; i++) {
            dest[p[i]] = src[p[i]];
        }
    }

    static cloneMaterials(mesh: BABYLON.AbstractMesh) {

        if (mesh.material) {
            let clonedMaterial = mesh.material.clone(mesh.material.id + "_clone");
            mesh.material = clonedMaterial;
        }

        mesh.getChildMeshes(false).forEach(function (m) {
            MaterialHelper.cloneMaterials(m);
        });
    }

}

/**
 * Mesh Helper
 * */

export class MeshHelper {

    static cloneMesh(meshId: string, cloneMesh: BABYLON.TransformNode | BABYLON.AbstractMesh) {

        let clonedMesh = cloneMesh.clone(meshId + "_clone", null, false) as BABYLON.AbstractMesh;
        clonedMesh.id = clonedMesh.name;
        clonedMesh.scaling.z = -1;
        clonedMesh.setEnabled(true);

        MaterialHelper.cloneMaterials(clonedMesh);

        return clonedMesh;

    }

    static instanceMesh(meshId: string, mesh: BABYLON.Mesh, root: BABYLON.TransformNode) {

        // var count = root.getChildMeshes(false, function (mesh) { return mesh.id === meshId + "_instance" }).length;

        let instanceMesh = mesh.createInstance(meshId + "_instance");
        instanceMesh.id = instanceMesh.name;
        instanceMesh.setEnabled(true);

        return instanceMesh;
    }
}

/**
 * Conversion Helper
 * */

export class ConversionHelper {

    static scale(value: any, factor: number) {
        if (typeof value === "number") {
            return value * factor;
        }
        if (value instanceof BABYLON.Vector3) {
            return value.scale(factor);
        }
    }

    static toVector3(value: any): BABYLON.Vector3 {
        if (value instanceof BABYLON.Vector3) {
            return value;
        }
        if (value instanceof Object) {
            if (value.x !== undefined &&
                value.y !== undefined &&
                value.z !== undefined) {
                return new BABYLON.Vector3(value.x, value.y, value.z);
            }
        }
        return new BABYLON.Vector3(value._x, value._y, value._z);
    }
}

/*
 * Data Helper
 * */

export class DataHelper {
    static find = (array, id) => {
        let result;
        array.some(o => result = o.id === id
            ? o
            : DataHelper.find(o.options || [], id)
        )
        return result;
    }

    static findDuplicate(arr) {
        arr.sort();

        let currentMostRepeatedNumber = arr[0];
        let currentNumberOfTimeRepeated = 1;

        for (var i = 0; i < arr.length; i++){

            //We initiate the current number we'll check in this iteration
            let currentRepeat = arr[i];
            let amountOfRepetition = 1;

            //We start a for to check the amount of times the number is repeated
            for(var y= 0; y < arr.length;y++){
                
                if(currentRepeat === arr[y]){
                    amountOfRepetition++;
                }  
            }
        
            if(amountOfRepetition > currentNumberOfTimeRepeated ){
                    currentNumberOfTimeRepeated = amountOfRepetition;
                    currentMostRepeatedNumber = currentRepeat ;
            }
        }

        return { value: currentMostRepeatedNumber, repeated: currentNumberOfTimeRepeated}
       // return "The most repeated number is " +currentMostRepeatedNumber + " , and he is repetead " + currentNumberOfTimeRepeated +" amount of times"
    }
}

/**
 *  Date Helper
 */

// @ts-ignore
//var DateTime = luxon.DateTime;

export class FormatHelper {

    static numberformat = new Intl.NumberFormat('en-GB');

    static formatDate = (date: Date) => {
        // @ts-ignore
        var dateFormatted = luxon.DateTime.fromISO(date);
        return dateFormatted.toFormat('dd.MM.yyyy hh:mm');
    }

    static formatNumber = (value: number) => {
        return FormatHelper.numberformat.format(value);
    }
}

/**
 * Product Helper
 * */

export class ProductHelper {

    static getFormattedString(product: Product | Accessory, values: Array<string>) {

        let joinableValues = [];

        values.forEach((key, index) => {
            if (product[key]) {
                joinableValues.push(product[key]);
            }
        });

        return joinableValues.join(" x ");

    }
}

/*
 * Series Helper 
 * */

export class SeriesHelper {

    static getSeriesDepth() {

        var seriesDepth = 0;

        var products = productLibrary.productsCategories.filter(function (p) { return productLibrary.getProductCategory(p.type.trim()) === "UPHOLSTRY" }).flatMap(function (p) { return p.products });
        var depths = products.map(function (p: Product) { return p.depth });

        seriesDepth = depths.reduce(
            (acc, item) =>
                depths.filter((v) => v === acc).length >=
                    depths.filter((v) => v === item).length
                    ? acc
                    : item,
            null
        );

        return seriesDepth;
    }

}

/**
 * Module Helper
 * */

export class ModuleHelper {

    scene: BABYLON.Scene;
    scaleFactor: number;
    entityHelper: EntityHelper;

    /*setScaleFactor(scaleFactor: number) {
        this.scaleFactor = scaleFactor
    }*/

    getFirstEntity(module: Module) {

        let self = this;

        if (!module || module.entities.length === 0)
            return;

        var entities = module.entities.filter(function (entity: Entity) {
            return productLibrary.getProductCategory(entity.category) === "UPHOLSTRY"
        });

        // if (entities.length === 1)
        //      return entities[0];

        var firstEntity = entities.find(function (entity: Entity) {
            return entity.connections.length === 1 &&
                ['CT01', 'CT04', 'CT06', 'CT10_R', 'CT11'].includes(self.getEntityConnectionType(module, entity))
        });

        if (!firstEntity)
            return entities[0];

        return firstEntity;
    }

    async generateModuleSummaryList(module: Module, configuration: Configuration): Promise<{ elements: ModuleSummaryElement[], customOptions: ModuleSummaryCustomOption[], accessories: ModuleSummaryAccessory[] }> {

        let self = this;

        let elements: ModuleSummaryElement[] = [];
        let accessories: ModuleSummaryAccessory[] = [];
        let customOptions: ModuleSummaryCustomOption[] = [];     

       const series = await  seriesLibrary.fetchSeriesByName(configuration.series);

            let leg = seriesLibrary.getSeriesLegPackage(series, configuration.data.leg);

            if (!configuration) {
                return { accessories: [], elements: [], customOptions: [] };
            }

            let currentMaterial = materialLibrary.getMaterialByProductNumber(configuration.data.material) as Material;

            // let firstEntity = this.getFirstEntity(module);
            //  if (firstEntity) {
            let sortedEntityIds = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").map(x => x.id);// this.generateSortedEntityIds(module, firstEntity, []);

            /*var standaloneEntities = module.entities.filter(function (entity: Entity) {
                return productLibrary.getProductCategory(entity.category) === "UPHOLSTRY" &&
                    entity.connections.length === 0
            });*/

            // sortedEntityIds = sortedEntityIds.filter(x => { return !standaloneEntities.map(e => e.id).includes(x) });
            //  sortedEntityIds.push(...standaloneEntities.map(x => x.id));
            for (let entityId of sortedEntityIds) {

                var entity = module.entities.find(x => x.id === entityId);
                var product = productLibrary.getProductByProductNo(entity.productNo)

                if (!product)
                    continue;

                var productVariant = productLibrary.getProductVariantByConnectionType(product, self.getEntityConnectionType(module, entity))
                var priceItem = productLibrary.getProductPrice(product.productNo, currentMaterial.priceGroup);

                if (!productVariant) {
                    productVariant = productLibrary.getFirstProductVariant(product);
                }
                        
                let extras: { name: string, productNo: string, price: number, optionId: string, optionValue: string }[] = [];
                
                leg.priceGroups.forEach(priceGroup => {
                    var option = series.customOptions.find(e => e.priceLists.some(e => e.priceGroup == priceGroup));

                var pricelist = option.priceLists.find(e => e.productType == "Leg Package" && e.priceGroup == priceGroup && e.items.includes(productVariant.name));

                    if (pricelist) {
                        const index = customOptions.findIndex(e => e.productNo == option.id);

                    if (index >= 0) {
                        customOptions[index].totalprice += pricelist.price.price ?? 0;
                        customOptions[index].amount += 1;
                    } else {
                        customOptions.push({
                            amount: 1,
                            currency: pricelist.price.currency ?? "",
                            productNo: option.name,
                            totalprice: pricelist.price.price ?? 0,
                            name: pricelist.description
                        })
                    }
                }
                
            });

                series.customOptions.forEach(option => {
                    if (option.perModule) {
                        const selection = entity.customOptions?.find(e => e.id == option.id);
                        if (selection) {
                            var pricelist = option.priceLists.find(e => e.productType == "Frame Cushion Item" && e.items.includes(productVariant.name));

                            /*
                            if (pricelist == undefined) {
                                pricelist = pricelists.find(e => e.items.length == 0);
                            }
                            */

                            if (pricelist) {
                                extras.push({ name: option.name, productNo: option.productNumber, price: pricelist.price.price ?? 0, optionId: option.id, optionValue: option.id });
                            }
                        }
                    } else {
                        const selection = configuration.data.customOptions?.find(e => e.id == option.id);
                        if (selection) {
                            var pricelists = option.priceLists.filter(e => ["Frame Cushion Item", "Leg Package"].includes(e.productType));
                            var pricelist = pricelists.find(e => e.items.includes(productVariant.name));

                            /*
                            if (pricelist == undefined) {
                                pricelist = pricelists.find(e => e.items.length == 0);
                            }
                            */

                            if (pricelist) {
                                const index = customOptions.findIndex(e => e.productNo == option.id);

                            if (index >= 0) {
                                customOptions[index].totalprice += pricelist.price.price ?? 0;
                                customOptions[index].amount += 1;
                            } else {
                                customOptions.push({
                                    amount: 1,
                                    currency: pricelist.price.currency ?? "",
                                    productNo: option.name,
                                    totalprice: pricelist.price.price ?? 0,
                                    name: pricelist.description
                                })
                            }
                        }
                    }
                }
            });

                const element = {
                    axProductNo: productVariant.axProductNo,
                    variantName: productVariant.name,
                    variantProductNo: productVariant.productNo,
                    productNo: product.productNo,
                    customerInfoText: productVariant.customerInfoText,
                    currency: priceItem.currency,
                    price: priceItem.price,
                    legPackageId: productVariant.legPackageID,
                    amount: 1,
                    entity: entityId,
                    extras
                }

                if (elements.filter(x => x.variantProductNo === element.variantProductNo).length > 0) {
                    var existingElement = elements.find(x => x.variantProductNo === element.variantProductNo);
                    existingElement.amount += 1;
                    existingElement.price += existingElement.price
                } else {
                    elements.push(element);
                }

                productVariant.recommendedCushions.forEach(cushion => {
                    const addOn = series.accessories.find(e => e.name == cushion.name);
                    if (addOn) {
                        const index = accessories.findIndex(e => e.productNo == addOn.productNumber);

                        if (index >= 0) {
                            accessories[index].amount += cushion.quantity;
                        } else {
                            accessories.push({
                                name: cushion.name,
                                amount: cushion.quantity,
                                currency: addOn.priceLists[0]?.currency ?? "",
                                productNo: addOn.productNumber,
                                price: addOn.priceLists[0]?.prices[currentMaterial.priceGroup] ?? 0,
                                extras: [],
                            })
                        }
                    }
                });
            };


            if (configuration.data.addons) {
                configuration.data.addons.forEach(a => {
                    const addOn = series.accessories.find(e => e.productNumber == a.productNo);
                    if (addOn) {
                        const index = accessories.findIndex(e => e.productNo == addOn.productNumber);

                        if (index >= 0) {
                            accessories[index].amount = Math.max(0, accessories[index].amount + a.amount);
                        } else if (a.amount > 0) {
                            accessories.push({
                                name: addOn.name,
                                amount: a.amount,
                                currency: addOn.priceLists[0]?.currency ?? "",
                                productNo: addOn.productNumber,
                                price: addOn.priceLists[0]?.prices[currentMaterial.priceGroup] ?? 0,
                                extras: [],
                            })
                        }
                    }
                });
            }

            accessories.forEach(accessory => {

                series.customOptions.forEach(option => {
                    const selection = configuration.data.customOptions?.find(e => e.id == option.id);
                    if (selection) {
                        var pricelist = option.priceLists.find(e => e.productType == "Accessories" && e.accessories.includes(accessory.name));

                        /*
                        if (pricelist == undefined) {
                            pricelist = pricelists.find(e => e.items.length == 0);
                        }
                        */

                        if (pricelist) {
                            accessory.extras.push({
                                name: option.name,
                                productNo: option.productNumber,
                                price: pricelist.price.price ?? 0,
                                optionId: option.id,
                                optionValue: option.id
                            });
                        }
                    }
                })
            })
        

        return { elements, accessories, customOptions };
    }

    getCurrency(module: Module, configuration: Configuration) {

        if (!productLibrary.productsCategories)
            return;

        var product = productLibrary.productsCategories[0].products[0];
        let currentMaterial = materialLibrary.getMaterialByProductNumber(configuration.data.material) as Material;
        var priceItem = productLibrary.getProductPrice(product.productNo, currentMaterial.priceGroup);
        return priceItem.currency;
    }

    async getTotalModulePrice(module: Module, configuration: Configuration) {
        var summaryList = await this.generateModuleSummaryList(module, configuration);
        var totalPrice = 0;
        var currency = this.getCurrency(module, configuration);

        for (let i = 0; i < summaryList.elements.length; i++) {
            const element = summaryList.elements[i];

            totalPrice += element.price;
            totalPrice += element.extras.reduce((p, n) => { return p + n.price }, 0);
        }
        for (let i = 0; i < summaryList.customOptions.length; i++) {
            const customOption = summaryList.customOptions[i];

            totalPrice += customOption.totalprice;
        }
        for (let i = 0; i < summaryList.accessories.length; i++) {
            const accessory = summaryList.accessories[i];

            totalPrice += accessory.price * accessory.amount;
            totalPrice += accessory.extras.reduce((p, n) => { return p + n.price }, 0) * accessory.amount;
        }

        return { totalPrice, currency };
    }

    generateEntityProductNumber(module: Module, entity: Entity) {

        let entityProduct = productLibrary.getProductByProductNo(entity.productNo);
        var entityConnectionType = this.getEntityConnectionType(module, entity);

        return `XXXXXX${entityConnectionType}${entityProduct.width}${entityProduct.height}`
    }

    generateSortedEntityIds(module: Module, entity: Entity, traversed: Array<string>) {

        let self = this;

        if (!entity)
            return traversed;

        if (traversed.length === 0) {
            traversed.push(entity.id);
        }

        let currentConnections = entity.connections;
        currentConnections.forEach(connection => {

            var nextEntity = module.entities.find(x => x.id === connection.entityId);
            if (!nextEntity)
                return traversed;

            if (!traversed.includes(nextEntity.id)) {
                entity = nextEntity;
                traversed.push(entity.id);
                return self.generateSortedEntityIds(module, nextEntity, traversed)
            }

        });

        return traversed;
    }


    getBaseConnectorName(connectorName: string) {

        if (connectorName.split("_").length > 2) {
            connectorName = connectorName.substring(0, connectorName.lastIndexOf("_"));
        }
        return connectorName;
    }

    getEntityByID(module: Module, id: string): Entity {
        return module.entities.find(e => { return e.id === id });
    }

    getConnectedEntityConnectors(module: Module, entity: Entity) {

        const arrConnectors = [] as Array<string>;

        entity.connections.forEach(connectedEntity => {

            var e = this.getEntityByID(module, connectedEntity.entityId);
            if (e) {
                e.connections.forEach(ec => {
                    if (ec.entityId === entity.id) {
                        arrConnectors.push(ec.id);
                    }
                });
            }

        });

        return arrConnectors;
    }

    getEntityConnectionType(module: Module, entity: Entity) {

        let self = this;

        var connectionType = "";

        if (!entity) {
            return connectionType;
        }

        var entityProduct = productLibrary.getProductByProductNo(entity.productNo);

        if (!entityProduct)
            return connectionType;

        const adjacentEntities = self.entityHelper.getAdjacentEntities(module, entity, self.scene);
        const childDirections = adjacentEntities.map(x => x.direction);
        const directions = childDirections.filter((x,i,arr) => arr.indexOf(x) === i).sort().toString()       

        var entityConnectionTypes = entityProduct.cushionPackages.find(x => x.isDefault).variants.map(x => x.connectionType)

        var entityWidth = ConversionHelper.scale(entityProduct.width, self.scaleFactor) as number;
        var entityDepth = ConversionHelper.scale(entityProduct.depth, self.scaleFactor) as number;
        var seriesDepth = ConversionHelper.scale(SeriesHelper.getSeriesDepth(), self.scaleFactor) as number;

        var connections = entity.connections.map(function (c) { return self.getBaseConnectorName(c.id) });
        var connectedConnectors = self.getConnectedEntityConnectors(module, entity);

        let hasConnections = (arr, target) => target.every(v => arr.includes(v));

        if (connections.length === 0) {
            connectionType = "CT00";
        }
        if (hasConnections(connections,["CT01_L"]) || hasConnections(connections,["CT01_R"])) {
            connectionType = "CT01";
        }
        if (hasConnections(connections,["CT03"])) {
            connectionType = "CT03";
        }
        if (hasConnections(connections,["CT04"])) {
            connectionType = "CT04";
        }
        if (hasConnections(connections,["CT05"])) {
            connectionType = "CT05";
        }
        if (hasConnections(connections,["CT06"])) {
            connectionType = "CT06";
        } 
        if (hasConnections(connections,["CT11"])) {
            connectionType = "CT11";
        } 
        if (hasConnections(connections,["CT37"])) {
            connectionType = "CT37";
        }                  
        if (hasConnections(connections,["CT03","CT04"])) {
            connectionType = "CT02";
        } 
        if (hasConnections(connections,["CT05","CT06"])) {
            connectionType = "CT07";
        }       
        if (hasConnections(connections,["CT05","CT01_R"])) {
            connectionType = "CT08";
        }
        if (hasConnections(connections,["CT06","CT01_L"])) {
            connectionType = "CT09";
        }       
        if (hasConnections(connections,["CT01_L","CT01_R"])) {
            connectionType = "CT10";
        }
        if (hasConnections(connections,["CT11","CT03"])) {
            connectionType = "CT25";
        }
        if (hasConnections(connections,["CT11","CT04"])) {
            connectionType = "CT26";
        }
        if (hasConnections(connections,["CT11","CT37"])) {
            connectionType = "CT36";
        }
        if (hasConnections(connections,["CT03","CT37"])) {
            connectionType = "CT38";
        }
        if (hasConnections(connections,["CT04","CT37"])) {
            connectionType = "CT39";
        }
        if (hasConnections(connections,["CT11","CT03","CT37"])) {
            connectionType = "CT34";
        }
        if (hasConnections(connections,["CT11","CT04","CT37"])) {
            connectionType = "CT35";
        }
        if (hasConnections(connections,["CT03","CT04","CT37"])) {
            connectionType = "CT40";
        }
        if (hasConnections(connections,["CT03","CT04","CT11"])) {
            connectionType = "CT41";
        }
        if (hasConnections(connections,["CT03","CT04","CT11","CT37"])) {
            connectionType = "CT12";
        }

        switch (entity.category) {

            case "CENTER_SECTION":
            case "CENTER_SECTION_LEFT_SHORTBACK":
            case "CENTER_SECTION_RIGHT_SHORTBACK":
            case "CENTER_SECTION_WO_BACK": {

                /*if (connections.includes("CT03")) {
                    connectionType = "CT03";
                }
                if (connections.includes("CT04")) {
                    connectionType = "CT04";
                }
                if (connections.includes("CT03") && connections.includes("CT04")) {
                    connectionType = "CT02";
                }

                if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }   */          
            }
                break;
            case "FOOTSTOOL": {

                /*if (entityDepth > entityWidth) {

                    if (connections.includes("CT03")) {
                        connectionType = "CT03";
                    }
                    if (connections.includes("CT04")) {
                        connectionType = "CT04";
                    }
                    if (connections.includes("CT03") && connections.includes("CT04")) {
                        connectionType = "CT02";
                    }

                } else {

                    if (connections.includes("CT03")) {
                        connectionType = "CT03";
                    }
                    if (connections.includes("CT04")) {
                        connectionType = "CT04";
                    }
                    if (connections.includes("CT03") && connections.includes("CT04")) {
                        connectionType = "CT02";
                    }

                    if (directions === "LEFT") {
                        connectionType = "CT03";
                    } 
                    if (directions === "RIGHT") {
                        connectionType = "CT04";
                    } 
                    if (directions === "LEFT,RIGHT") {
                        connectionType = "CT02";
                    }               
                    if (directions === "BACK") {
                        connectionType = "CT11";
                    } 
                    if (directions === "FRONT") {
                        connectionType = "CT37";
                    }
                    if (directions === "BACK,LEFT") {
                        connectionType = "CT25";
                    }
                    if (directions === "BACK,RIGHT") {
                        connectionType = "CT26";
                    }
                    if (directions === "BACK,FRONT,LEFT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT,RIGHT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT") {
                        connectionType = "CT36";
                    }
                    if (directions === "FRONT,LEFT") {
                        connectionType = "CT38";
                    }
                    if (directions === "FRONT,RIGHT") {
                        connectionType = "CT39";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "BACK,LEFT,RIGHT") {
                        connectionType = "CT41";
                    }
                    if (directions === "BACK,FRONT,LEFT,RIGHT") {
                        connectionType = "CT12";
                    }     
                }        */

               /* if (productLibrary.hasProductVariants(entityProduct, ["CT11"])) {
                    if (connections.includes("CT04") && connections.length === 1 && connectedConnectors.includes("CT05")) {
                        connectionType = "CT11";
                    }
                    if (connections.includes("CT03") && connections.length === 1 && connectedConnectors.includes("CT06")) {
                        connectionType = "CT11";
                    }
                }

                if (entityWidth === seriesDepth && entityDepth === seriesDepth) {

                    if ((connections.includes("CT04") && connections.includes("CT09_L")) || (connections.includes("CT03") && connections.includes("CT09_R"))) {
                        connectionType = "CT10";
                    }
                    if ((connections.includes("CT04") && connections.includes("CT10_L"))) {
                        connectionType = "CT08";
                    }
                    if ((connections.includes("CT03") && connections.includes("CT10_R"))) {
                        connectionType = "CT09";
                    }
                }*/
            }
                break;
            case "CLOSING_SECTION_LEFT":
            case "CLOSING_SECTION_LEFT_SHORTBACK":
            case "CLOSING_SECTION_LEFT_WO_ARM": {

               /* if (connections.includes("CT03")) {
                    connectionType = "CT03";
                }
                if (connections.includes("CT04")) {
                    connectionType = "CT04";
                }
                if (connections.includes("CT03") && connections.includes("CT04")) {
                    connectionType = "CT02";
                }

                if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }             

                if (connections.includes("CT01_R")) {
                    connectionType = "CT01";
                }
                if (connections.includes("CT05")) {
                    connectionType = "CT05";
                }                
                if (connections.includes("CT01_R") && connections.includes("CT05")) {
                    connectionType = "CT08";
                }*/
            }
                break;
            case "CLOSING_SECTION_RIGHT":
            case "CLOSING_SECTION_RIGHT_SHORTBACK":
            case "CLOSING_SECTION_RIGHT_WO_ARM": {

                /*if (connections.includes("CT03")) {
                    connectionType = "CT03";
                }
                if (connections.includes("CT04")) {
                    connectionType = "CT04";
                }
                if (connections.includes("CT03") && connections.includes("CT04")) {
                    connectionType = "CT02";
                }

                if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }             

                if (connections.includes("CT01_L")) {
                    connectionType = "CT01";
                }
                if (connections.includes("CT06")) {
                    connectionType = "CT06";
                }
                if (connections.includes("CT01_L") && connections.includes("CT06")) {
                    connectionType = "CT09";
                }*/
            }
                break;
            case "CHAISELONG_LEFT":
            case "CHAISELONG_LEFT_WO_ARM": {

              /*  if (connections.includes("CT03")) {
                    connectionType = "CT03";
                }
                if (connections.includes("CT04")) {
                    connectionType = "CT04";
                }
                if (connections.includes("CT03") && connections.includes("CT04")) {
                    connectionType = "CT02";
                }

                if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }             

                if (connections.includes("CT01_R")) {
                    connectionType = "CT01";
                }*/
            }
                break;
            case "CHAISELONG_RIGHT":
            case "CHAISELONG_RIGHT_WO_ARM": {

               /* if (connections.includes("CT03")) {
                    connectionType = "CT03";
                }
                if (connections.includes("CT04")) {
                    connectionType = "CT04";
                }
                if (connections.includes("CT03") && connections.includes("CT04")) {
                    connectionType = "CT02";
                }

                 if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }             

                if (connections.includes("CT01_L")) {
                    connectionType = "CT01";
                }*/
            }
                break;
            case "CORNER_SECTION":
            case "CLOSING_CORNER_SECTION_LEFT":
            case "CLOSING_CORNER_SECTION_RIGHT": {

                /*if (directions === "LEFT") {
                    connectionType = "CT03";
                } 
                if (directions === "RIGHT") {
                    connectionType = "CT04";
                } 
                if (directions === "LEFT,RIGHT") {
                    connectionType = "CT02";
                }               
                if (directions === "BACK") {
                    connectionType = "CT11";
                } 
                if (directions === "FRONT") {
                    connectionType = "CT37";
                }
                if (directions === "BACK,LEFT") {
                    connectionType = "CT25";
                }
                if (directions === "BACK,RIGHT") {
                    connectionType = "CT26";
                }
                if (directions === "BACK,FRONT,LEFT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT,RIGHT") {
                    connectionType = "CT34";
                }
                if (directions === "BACK,FRONT") {
                    connectionType = "CT36";
                }
                if (directions === "FRONT,LEFT") {
                    connectionType = "CT38";
                }
                if (directions === "FRONT,RIGHT") {
                    connectionType = "CT39";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "FRONT,LEFT,RIGHT") {
                    connectionType = "CT40";
                }
                if (directions === "BACK,LEFT,RIGHT") {
                    connectionType = "CT41";
                }
                if (directions === "BACK,FRONT,LEFT,RIGHT") {
                    connectionType = "CT12";
                }             

                if (connections.includes("CT10_L")) {
                    connectionType = "CT10_L";
                }
                if (connections.includes("CT10_R")) {
                    connectionType = "CT10_R";
                }*/

            }
                break;
            case "SOFA":
            case "SOFA_LEFT":
            case "SOFA_RIGHT":
            case "SOFA_SECTIONAL": {

               /* if (connections.includes("CT05")) {
                    connectionType = "CT05";
                }
                if (connections.includes("CT06")) {
                    connectionType = "CT06";
                }
                if (connections.includes("CT05") && connections.includes("CT06")) {
                    connectionType = "CT07";
                }*/
            }
                break;
            case "ARM":
            case "ARM_LEFT":
            case "ARM_RIGHT": {

                if ([148882,157547,157546].includes(entityProduct.series)) {

                    if (connections.includes("CT03")) {
                        connectionType = "CT03";
                    }
                    if (connections.includes("CT04")) {
                        connectionType = "CT04";
                    }
                    if (connections.includes("CT03") && connections.includes("CT04")) {
                        connectionType = "CT02";
                    }
                    if (connections.includes("CT44")) {
                        connectionType = "CT44";
                    }
                    if (connections.includes("CT45")) {
                        connectionType = "CT45";
                    }
                    if (connections.includes("CT44") && connections.includes("CT45")) {
                        connectionType = "CT46";
                    }
                    if (connections.includes("CT44_center")) {
                        connectionType = "CT47";
                    }
                    if (connections.includes("CT45_center")) {
                        connectionType = "CT48";
                    }
                    if (connections.includes("CT44_center") && connections.includes("CT45_center")) {
                        connectionType = "CT49";
                    }
                }
            }
                break;
            case "BACK": {

                if ([148882,157547,157546].includes(entityProduct.series)) {
                   
                    if (connections.includes("CT37") && connections.includes("CT36")) {
                        connectionType = "CT36";
                    }
                    if (connections.includes("CT37") && connections.includes("CT38") && connections.includes("CT39")) {
                        connectionType = "CT40";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT38")) {
                        connectionType = "CT34";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT39")) {
                        connectionType = "CT35";
                    }
                    if (connections.includes("CT36") && connections.includes("CT37") && connections.includes("CT38") && connections.includes("CT39")) {
                        connectionType = "CT12";
                    }
                }

            }
                break;
            case "SEATFRAME": {

                if ([148882,157547,157546].includes(entityProduct.series)) {

                    if (directions === "LEFT") {
                        connectionType = "CT03";
                    } 
                    if (directions === "RIGHT") {
                        connectionType = "CT04";
                    } 
                    if (directions === "LEFT,RIGHT") {
                        connectionType = "CT02";
                    }               
                    if (directions === "BACK") {
                        connectionType = "CT11";
                    } 
                    if (directions === "FRONT") {
                        connectionType = "CT37";
                    }
                    if (directions === "BACK,LEFT") {
                        connectionType = "CT25";
                    }
                    if (directions === "BACK,RIGHT") {
                        connectionType = "CT26";
                    }
                    if (directions === "BACK,FRONT,LEFT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT,RIGHT") {
                        connectionType = "CT34";
                    }
                    if (directions === "BACK,FRONT") {
                        connectionType = "CT36";
                    }
                    if (directions === "FRONT,LEFT") {
                        connectionType = "CT38";
                    }
                    if (directions === "FRONT,RIGHT") {
                        connectionType = "CT39";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "FRONT,LEFT,RIGHT") {
                        connectionType = "CT40";
                    }
                    if (directions === "BACK,LEFT,RIGHT") {
                        connectionType = "CT41";
                    }
                    if (directions === "BACK,FRONT,LEFT,RIGHT") {
                        connectionType = "CT12";
                    } 
                }
             
            }
                break;
        }

        if (!entityConnectionTypes.includes(connectionType)) {
            connectionType = entityConnectionTypes[0];
        }

        return connectionType;
    }
}



/* *
 * Leg Selector 
 * */

/*class LegSelector {

    YUI: YUIContext
    seriesId: number
    leg: Leg
    legVariant: LegVariant
    selectorNode: any
    seriesLibrary: SeriesLibrary

    constructor(YUI: YUIContext, seriesLibrary: SeriesLibrary, selectorNode: any) {
        this.YUI = YUI;
        this.seriesLibrary = seriesLibrary;
        this.selectorNode = selectorNode;
    }

    public empty() {
        let itemList = this.selectorNode.one(".leg-selector-items ul");
        itemList.empty();
    }

    public update(YUI: YUIContext) {

        let self = this;

        self.empty();

        let itemList = this.selectorNode.one(".leg-selector-items ul");

        let series = this.seriesLibrary.getSeries(this.seriesId);
        series.legs.forEach(function (leg: Leg) {

            var legNode = YUI.Node.create("<li>");

            if (this.leg) {
                if (this.leg.model === leg.model) {
                    legNode.addClass("is-active");
                }
            }

            legNode.setAttribute("data-id", leg.model);

            var imageWrapperNode = YUI.Node.create("<div class='module__image'>");

            var nameWrapperNode = YUI.Node.create("<div class='module__name'>");

            var legImage = YUI.Node.create("<img>");
            legImage.setAttribute("src", leg.thumbnail);
            imageWrapperNode.appendChild(legImage);

            legImage.getDOMNode().onload = function (e) {
                legNode.addClass("show");
            }
            legImage.getDOMNode().onerror = function (e) {
                legImage.setAttribute("src", "");
            }

            var legType = YUI.Node.create("<div>");
            legType.setHTML(leg.name);
            nameWrapperNode.appendChild(legType);

            legNode.appendChild(imageWrapperNode);
            legNode.appendChild(nameWrapperNode);


            itemList.appendChild(legNode);

        });

    }
}*/

export class MaterialSelector {

    YUI: YUIContext
    element: any
    materialLibrary: MaterialLibrary
    scrollView: any
    configuration: Configuration
    materialGroup: string
    selectCallBack: any
    selectGroupCallback: any

    constructor(YUI: YUIContext, materialLibrary: MaterialLibrary, element: any) {
        this.YUI = YUI;
        this.materialLibrary = materialLibrary;
        this.element = element;

        this.init(YUI);
    }

    public init(YUI: YUIContext) {

        let self = this;

        this.scrollView = new YUI.ScrollView({
            srcNode: this.element.one(".selector__items"),
            axis: "y",
            height: 100,
            flick: {
                minDistance: 10,
                minVelocity: 0.3,
                axis: "y"
            }
        });

        this.scrollView.render();

        window.addEventListener("resize", function (e) {
            self.resize();
        });

        this.element.delegate("click", function (e) {

            if (Math.abs(self.scrollView.lastScrolledAmt) < 2) {
                var elem = e.currentTarget;

                self.element.all(".selector__items li").each(function (e) {
                    e.removeClass("is-active");
                });

                elem.addClass("is-active");

                var materialProductNumber = elem.getData("id");

                self.configuration.data["material"] = materialProductNumber;
                var currentMaterial = materialLibrary.getMaterialByProductNumber(materialProductNumber);

                var folderName = currentMaterial.type + "/" + currentMaterial.group;
                let selectedMaterialElem = self.element.one(".material-selector__material__image");
                selectedMaterialElem.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + folderName + "/" + currentMaterial.productNumber + "_thumb.jpg')");
                let selectedName = self.element.one(".material-selector__material__name");
                selectedName.setHTML(currentMaterial.name + "(pricegroup: " + currentMaterial.priceGroup + ")");

                if (typeof (self.selectCallBack) === "function") {
                    self.selectCallBack();
                }
            }

        }, ".selector__items li");

        this.element.delegate("change", function (e) {

            self.configuration.data.options["removableCover"] = !self.configuration.data.options["removableCover"];
            window.dispatchEvent(new Event("saveconfigurations"));

        }, ".cover input");

        this.element.delegate("click", function (e) {

            e.preventDefault();

            if (typeof (self.selectGroupCallback) === "function") {
                self.selectGroupCallback();
            }

        }, ".material-selector__items__group__select");

    }

    public setConfiguration(configuration: Configuration) {
        this.configuration = configuration;
        this.update();
    }

    public setMaterialGroup(materialGroup) {
        this.materialGroup = materialGroup;
        this.update();
    }

    public resize() {

        this.scrollView.scrollTo(0, 0);
        this.scrollView._uiDimensionsChange();

        let contentElem = this.scrollView.get("contentBox");

        if (contentElem.getDOMNode().offsetHeight >= contentElem.getDOMNode().scrollHeight) {
            this.scrollView.scrollbars.hide();
            this.scrollView.disable();
        } else {
            this.scrollView.enable();
            this.scrollView.scrollbars.show();
            this.scrollView._uiDimensionsChange();
        }
    }

    public empty() {
        let itemList = this.element.one(".selector__items");
        itemList.empty();
    }

    public update() {

        let self = this;
        self.empty();

        var material = this.materialLibrary.materials[0];
        if (material) {
            var group = material.group;

            if (self.materialGroup) {
                group = self.materialGroup;
            }

            if (self.configuration) {
                if (self.configuration.data["material"]) {
                    material = materialLibrary.getMaterialByProductNumber(self.configuration.data["material"]);
                } else {
                    self.configuration.data["material"] = material.productNumber;
                }
            }

            if (!self.materialGroup) {
                group = material.group;
            }

            var folderName = material.type + "/" + material.group;
            let selectedMaterialElem = self.element.one(".material-selector__material__image");
            selectedMaterialElem.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + folderName + "/" + material.productNumber + "_thumb.jpg')");
            let selectedName = self.element.one(".material-selector__material__name");
            selectedName.setHTML(material.name + " (pricegroup: " + material.priceGroup + ")");

            var groupedMaterials = materialLibrary.materials.filter(function (material) { return material.group === group });

            let itemsElem = this.element.one(".selector__items");

            let materialElem = this.element.one(".material-selector__material__name");
            materialElem.setHTML(material.name + " (pricegroup: " + material.priceGroup + ")");

            let groupElem = this.element.one(".material-selector__group__name");
            groupElem.setHTML(self.materialGroup);

            groupedMaterials.forEach(function (groupedMaterial) {

                var materialNode = self.YUI.Node.create("<li>");
                materialNode.setAttribute("data-id", groupedMaterial.productNumber);

                if (material.productNumber === groupedMaterial.productNumber) {
                    materialNode.addClass("is-active");
                }

                var itemContent = self.YUI.Node.create("<div>");
                itemContent.setStyle("background-image", "url('" + AppSettings.Paths.BaseMaterialsPath + groupedMaterial.type + "/" + groupedMaterial.group + "/" + groupedMaterial.productNumber + "_thumb.jpg')");

                materialNode.appendChild(itemContent);
                itemsElem.appendChild(materialNode);

            });
        }

        //Has Cover?
        // let removableCoverElem = self.element.one(".cover input");
        // removableCoverElem.set("checked", self.configuration.data.options["removableCover"]);

        this.resize();
    }
}

/* *
 * Product Selector
 * */

export class ProductSelector {

    YUI: YUIContext
    element: any
    material: any
    dragdropHandler: DragDropHandler
    productLibrary: ProductLibrary
    selectedCategory: ProductCategory
    scrollViewNav: any
    scrollViewItems: any

    constructor(YUI: YUIContext, dragdropHandler: DragDropHandler, productLibrary: ProductLibrary, element: any) {
        this.YUI = YUI;
        this.dragdropHandler = dragdropHandler;
        this.productLibrary = productLibrary;
        this.element = element;        
        this.material = {} as Material;

        this.init(YUI);
    }

    public init(YUI: YUIContext) {

        let self = this;

        var navScrollLeft = 0;
        var itemsScrollLeft = 0;

        const navElement = document.querySelector('.selector__tabs__nav')! as HTMLElement;
        const itemsElement = document.querySelector('.selector__items')! as HTMLElement;

        if (!isTouchDevice()) {

            const navGestures = new Hammer(navElement);
            navGestures.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
            navGestures.on('panend', (e) => {              
                navScrollLeft = navElement.scrollLeft;               
            });
            navGestures.on('panmove', (e) => {               
                if ((e.srcEvent as PointerEvent).buttons > 0) {
                    navElement.scrollLeft = navScrollLeft - e.deltaX;       
                }
            });   

            const itemsGestures = new Hammer(itemsElement);
            itemsGestures.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
            itemsGestures.on('panend', (e) => {              
                itemsScrollLeft = itemsElement.scrollLeft;               
            });
            itemsGestures.on('panmove', (e) => {               
                if ((e.srcEvent as PointerEvent).buttons > 0) {
                    itemsElement.scrollLeft = itemsScrollLeft - e.deltaX;       
                }
            });  

        } else {
            navElement.style.touchAction = 'auto'; 
            itemsElement.style.touchAction = 'auto'; 
        }

       /* this.scrollViewNav = new YUI.ScrollView({
            srcNode: this.element.one(".selector__tabs__nav__wrapper"),
            axis: "x",
            width: "100%",
            flick: {
                minDistance: 10,
                minVelocity: 0.3,
                axis: "x"
            }
        });

        this.scrollViewItems = new YUI.ScrollView({
            srcNode: this.element.one(".selector__items__wrapper"),
            axis: "x",
            width: "100%",
            flick: {
                minDistance: 10,
                minVelocity: 0.3,
                axis: "x"
            }
        });

        this.scrollViewNav.render();
        this.scrollViewItems.render();*/

        window.addEventListener("resize", function (e) {
            self.resize();
        });

        this.element.delegate("click", function (e) {

            //if (Math.abs(self.scrollViewNav.lastScrolledAmt) < 2) {

                /* var productCategoriesItems = self.YUI.all(".selector__tabs__nav li")
                 productCategoriesItems.each(function (pc,index) {
                     pc.removeClass(".active")
                 });*/

                var target = e.currentTarget;
                var category = target.getAttribute("data-category");

                self.selectedCategory = self.productLibrary.productsCategories.find(x => x.type === category);
                self.update();
            //}

        }, ".selector__tabs__nav li");
    }

    public resize() {

        //  this.scrollViewNav.scrollTo(0, 0);
        //this.scrollViewItems.scrollTo(0, 0);

        let navListElem = this.element.one(".selector__tabs__nav")
        const navTotalWidth = Object.values(navListElem.getDOMNode().childNodes).reduce((total, i: any) => total + i.offsetWidth, 0)

        let itemsListElem = this.element.one(".selector__items")
        const itemsTotalWidth = Object.values(itemsListElem.getDOMNode().childNodes).reduce((total, i: any) => total + i.offsetWidth, 0)

        // this.scrollViewNav._uiDimensionsChange();
        // this.scrollViewItems._uiDimensionsChange();
       /* let navContentElem = this.scrollViewNav.get("contentBox");
        if (navContentElem.getDOMNode().offsetWidth > navTotalWidth) {
            this.scrollViewNav.lastScrolledAmt = 0;
            this.scrollViewNav.scrollbars.hide();
            this.scrollViewNav.disable();
        } else {
            this.scrollViewNav.enable();
            this.scrollViewNav.scrollbars.show();
            this.scrollViewNav._uiDimensionsChange();
        }*/

        /*let itemsContentElem = this.scrollViewItems.get("contentBox");
        if (itemsContentElem.getDOMNode().offsetWidth > itemsTotalWidth) {
            this.scrollViewItems.lastScrolledAmt = 0;
            this.scrollViewItems.scrollbars.hide();
            this.scrollViewItems.disable();
        } else {
            this.scrollViewItems.enable();
            this.scrollViewItems.scrollbars.show();
            this.scrollViewItems._uiDimensionsChange();
        }*/
    }

    public reset() {

       // this.scrollViewNav.scrollTo(0, 0);
       // this.scrollViewItems.scrollTo(0, 0);
        this.selectedCategory = this.productLibrary.productsCategories.filter(function (pc) { return pc.products })[0];
        //this.categories = [];
    }

    public empty() {

        let navList = this.element.one(".selector__tabs__nav");
        navList.empty();

        let itemList = this.element.one(".selector__items");
        itemList.empty();
    }

    public update() {

        let self = this;
        self.empty();

        var productCategories = this.productLibrary.productsCategories.filter(function (product) { return productLibrary.getProductCategory(product.type) === "UPHOLSTRY" });

        let navElem = this.element.one(".selector__tabs__nav");
        productCategories.forEach(function (category) {
            var productNode = self.YUI.Node.create("<li class='selector__tabs__navitem'>");
            productNode.setAttribute("data-type", "category");
            productNode.setAttribute("data-category", category.type);
            productNode.setAttribute("data-name", category.name);

            if (self.selectedCategory === category) {
                productNode.addClass("active")
            }

            productNode.setHTML(category.name);
            navElem.appendChild(productNode);
        });

        let itemsElem = this.element.one(".selector__items");
        this.element.addClass("show");

        var categoryProducts = productCategories.map(x => {
            return {
                id: x.id,
                type: x.type,
                name: x.name,
                productNo: null,
                products: x.products
            }
        }) as any[];

        if (self.selectedCategory) {
            let filteredProducts = productCategories.filter(function (productCategory) { return productCategory.type === self.selectedCategory.type })[0];
            categoryProducts = filteredProducts.products;
        } else {
            self.selectedCategory = productCategories[0];
        }

        categoryProducts.forEach(function (product) {

            var categoryProductNode = self.YUI.Node.create("<li class='selector__items__item product'>");
            if (categoryProductNode.getDOMNode() !== null) {

                categoryProductNode.setAttribute("data-type", "product");
                //categoryProductNode.addClass("draggable show");
                categoryProductNode.setAttribute("data-productnumber", product.productNo);

                var wrapperElem = self.YUI.Node.create("<div class='selector__items__item__wrapper'>");

                var imageElem = self.YUI.Node.create("<div class='selector__items__item__image'>");
                imageElem.setAttribute("data-productnumber", product.productNo);

                var nameElem = self.YUI.Node.create("<div class='selector__items__item__name'>");

                var productImage = self.YUI.Node.create("<img>");
               // productImage.setAttribute("draggable", true);               
                productImage.setAttribute("src", product.thumbnail);
                imageElem.appendChild(productImage);

                if (productImage) {
                    productImage.getDOMNode().onload = function (e) {
                        var currentCategoryProductNode = self.element.one(".selector__items__item.product[data-productnumber='" + product.productNo + "']")
                        if (currentCategoryProductNode.getDOMNode() !== null)
                            currentCategoryProductNode.addClass("show");
                    }
                    productImage.getDOMNode().onerror = function (e) {
                        productImage.setAttribute("src", "/assets/series/1/products/171/thumbnail");
                    }
                }

                var productType = self.YUI.Node.create("<div>");
                productType.setHTML(product.name);
                nameElem.appendChild(productType);

                wrapperElem.appendChild(imageElem);
                wrapperElem.appendChild(nameElem);
                categoryProductNode.appendChild(wrapperElem);
                
                self.dragdropHandler.createDraggable(product.productNo, imageElem.getDOMNode());

                itemsElem.appendChild(categoryProductNode);
            }
        });

        this.resize();
    }
}

/**
 * Options Selector
 */

export class OptionsSelector {

    YUI: YUIContext
    options: Array<PriceOption>
    element: any
    scrollView: any
    path: Array<string>
    configuration: Configuration
    series: SeriesData

    constructor(YUI: YUIContext, element: any) {
        this.YUI = YUI
        this.element = element
        this.path = []

        this.init();
    }

    public resize() {

        this.scrollView.scrollTo(0, 0);

        let itemsElem = this.element.one(".selector__items")
        const totalWidth = Object.values(itemsElem.getDOMNode().childNodes).reduce((total, i: any) => total + i.offsetWidth, 0)

        let contentElem = this.scrollView.get("contentBox");
        if (contentElem.getDOMNode().offsetWidth > totalWidth) {
            this.scrollView.scrollbars.hide();
            this.scrollView.disable();
        } else {
            this.scrollView.enable();
            this.scrollView.scrollbars.show();
            this.scrollView._uiDimensionsChange();
        }
    }

    public reset() {
        this.scrollView.scrollTo(0, 0);
        this.path = [];
    }

    public setConfiguration(configuration: Configuration) {
        this.configuration = configuration;
    }

    public setSeries(series: SeriesData) {
        this.series = series;
    }

    public setupMockData() {

        let self = this;

        //Options mockup!
        this.options = [
            {
                id: "legs",
                name: "Legs",
                dataType: "container",
                thumbnail: "",
                options: [
                    /*{
                        id: "legAlternativeHeight",
                        name: "Alternativ Height",
                        dataType: "radio",
                        thumbnail: "",
                        options: [
                            { id: "-5", label: "-5" },
                            { id: "-4", label: "-4" },
                            { id: "-3", label: "-3" },
                            { id: "-2", label: "-2" },
                            { id: "-1", label: "-1" },
                            { id: "+1", label: "+1" },
                            { id: "+1", label: "+1" },
                    },*/
                    {
                        id: "legAlternativeHeight",
                        name: "Alternativ Height",
                        dataType: "boolean",
                        thumbnail: "",
                        options: []
                    },
                    {
                        id: "leg",
                        name: "Leg",
                        dataType: "select",
                        thumbnail: "",
                        options: []// self.series.legPackages.map(function (leg) { return { id: leg.id, name: leg.name, dataType: "select-value", thumbnail: leg.thumbnail, options: [] } })
                    },
                    {
                        id: "legColor",
                        name: "Color",
                        dataType: "select",
                        thumbnail: "",
                        options: []// self.series.legs.map(function (leg) { return { id: leg.id, name: leg.name, dataType: "select-value", thumbnail: leg.thumbnail, options: [] } })
                    }
                ]
            },
            {
                id: "backcushion",
                name: "Backcushion",
                dataType: "container",
                thumbnail: "",
                options: [
                    {
                        id: "firmerbackcushion",
                        name: "Firmer Backcushion",
                        dataType: "boolean",
                        thumbnail: "",
                        options: []
                    }
                ]
            },
            {
                id: "filling",
                name: "Filling",
                dataType: "container",
                thumbnail: "",
                options: [
                    {
                        id: "firmness",
                        name: "Firmness",
                        dataType: "radio",
                        thumbnail: "",
                        options: [
                            { id: "verysoft", name: "Very soft", dataType: "radio-value", thumbnail: "" },
                            { id: "soft", name: "Soft", dataType: "radio-value", thumbnail: "" },
                            { id: "firmer", name: "Firmer", dataType: "radio-value", thumbnail: "" },
                            { id: "veryfirm", name: "Very firm", dataType: "radio-value", thumbnail: "" },
                        ]
                    }
                ]
            },
            {
                id: "pillows",
                name: "Pillows",
                dataType: "container",
                thumbnail: "",
                options: [
                    {
                        id: "hypoallergenicpillow",
                        name: "Hypoallergenic Pillow",
                        dataType: "boolean",
                        thumbnail: "",
                        options: []
                    }
                ]
            }
        ] as unknown as Array<PriceOption>;
    }

    public init() {

        let self = this;

        this.scrollView = new this.YUI.ScrollView({
            srcNode: this.element.one(".selector__items"),
            axis: "x",
            width: "100%",
            flick: {
                minDistance: 10,
                minVelocity: 0.3,
                axis: "x"
            }
        });

        this.scrollView.render();

        window.addEventListener("resize", function (e) {
            self.resize();
        });

        this.element.delegate("click", async function (e) {

            let series = await seriesLibrary.fetchSeriesByName(self.configuration.series);

            if (Math.abs(self.scrollView.lastScrolledAmt) < 2) {

                let elem = e.currentTarget;
                let dataType = elem.getAttribute("data-datatype");
                let parentId = elem.getAttribute("data-parentid");
                let id = elem.getAttribute("data-id");

                switch (dataType) {
                    case "container": {
                        self.path.push(id);
                        self.update(id);
                    }
                        break;
                    case "boolean": {
                        self.configuration.data.options[id] = !self.configuration.data.options[id];
                        self.update(self.path[self.path.length - 1]);
                    }
                        break;
                    case "radio": {
                        // self.path.push(id);
                        self.update(id);
                    }
                        break;
                    case "select":
                        {
                            self.path.push(id);
                            self.update(id);
                        }
                        break;
                    case "select-value":
                        {
                            self.path.pop();
                            self.update(self.path[self.path.length - 1]);
                        }
                        break;
                    case "radio-value":
                        {
                            self.configuration.data.options[parentId] = id
                            self.update(self.path[self.path.length - 1]);
                        }
                        break;
                }

                window.dispatchEvent(new Event("saveconfigurations"));
            }

        }, ".selector__items__item");

        this.element.delegate("click", function (e) {

            e.preventDefault();
            self.path.pop();

            let id = null;
            if (self.path.length > 0) {
                id = self.path[self.path.length - 1];
            }

            self.update(id);

        }, ".selector__nav__back");
    }

    public empty() {

        let itemsElem = this.element.one(".selector__items")
        itemsElem.empty();

    }

    public createImageElem(option: PriceOption, optionElem: any) {

        let self = this;
        var imageElem = self.YUI.Node.create("<div class='selector__items__item__image'>");

        if (optionElem) {

            var img = self.YUI.Node.create("<img>");
            img.setAttribute("src", option.thumbnail);
            img.getDOMNode().onload = function (e) {
                optionElem.addClass("show");
            }
            img.getDOMNode().onerror = function (e) {
                img.setAttribute("src", "/assets/series/1/products/171/thumbnail");
            }
            imageElem.appendChild(img);
        }

        return imageElem;
    }

    public createNameElem(option: PriceOption) {

        let self = this;

        var nameElem = self.YUI.Node.create("<div class='selector__items__item__name'>");
        nameElem.setHTML(option.name);

        return nameElem;
    }

    public createSelectLabel(option: PriceOption) {

        let self = this;

        var labelElem = self.YUI.Node.create("<span class='selector__items__item__selectlabel'>")
        labelElem.setHTML(option.name);

        return labelElem;
    }

    public update(id?: string) {

        let self = this;
        self.empty();

        self.scrollView.lastScrolledAmt = 0;

        let itemsElem = this.element.one(".selector__items");

        this.element.removeClass("show");
        if (this.path.length > 0) {
            this.element.addClass("show");
        }

        let currentOption = null;
        let currentOptions = this.options;
        if (id) {
            currentOption = DataHelper.find(this.options, id);
            currentOptions = currentOption.options;
        }

        currentOptions.forEach(function (option, index) {

            var optionElem = self.YUI.Node.create("<li class='selector__items__item option'>");
            optionElem.setAttribute("data-id", option.id);

            var wrapperElem = self.YUI.Node.create("<div class='selector__items__item__wrapper'>");

            optionElem.setAttribute("data-datatype", option.dataType);

            switch (option.dataType) {
                case "container": {
                    if (option.options.length === 0) {
                        optionElem.setAttribute("disabled", "disabled");
                    }
                    wrapperElem.appendChild(self.createImageElem(option, optionElem));
                }
                    break;
                case "boolean": {
                    optionElem.setAttribute("data-value", self.configuration.data.options[option.id] || false);
                    optionElem.addClass(self.configuration.data.options[option.id] ? "is-active" : "");

                    wrapperElem.appendChild(self.createImageElem(option, optionElem));
                }
                    break;
                case "radio": {
                    optionElem.addClass("show");
                    let imageElem = self.createImageElem(option, optionElem);
                    wrapperElem.appendChild(imageElem);
                }
                    break;
                case "single": {

                    optionElem.addClass("show");

                    let value = self.configuration.data.options[option.id] ?? 0;

                    var sliderContainer = self.YUI.Node.create("<div class='slider-container'>");

                    var slider = self.YUI.Node.create("<input class='slider' list='tickmarks' step='1' value='" + value + "' type='range'>");
                    slider.set("min", 0);
                    slider.set("max", option.options.length - 1);
                    slider.set("value", value)

                    var sliderValue = self.YUI.Node.create("<label>");
                    sliderValue.setHTML(value);

                    slider.on("change", function (e) {

                        var value = parseInt(slider.get("value"));
                        self.configuration.data.options[option.id] = value;
                        sliderValue.setHTML(option.options[value]);
                        window.dispatchEvent(new Event("saveconfigurations"));

                    });

                    sliderContainer.appendChild(sliderValue);
                    sliderContainer.appendChild(slider);

                    wrapperElem.appendChild(sliderContainer);

                }
                    break;
                case "select": {

                    optionElem.addClass("show");

                    let selectedOption: PriceOption;

                    let imageElem = self.createImageElem(selectedOption, optionElem);
                    imageElem.appendChild(self.createSelectLabel(selectedOption));

                    wrapperElem.appendChild(imageElem);

                }
                    break;
                case "select-value": {

                    optionElem.addClass("show");
                    optionElem.setAttribute("data-parentid", currentOption.id);

                    var defaultLeg = seriesLibrary.getSeriesLeg(self.series, "standard");

                    var value = self.configuration.data.options[currentOption.id] || defaultLeg.productNumber;

                    if (option.id === value) {
                        optionElem.addClass("is-active");
                    }

                    wrapperElem.appendChild(self.createImageElem(option, optionElem));


                }
                    break;
                case "radio-value": {

                    let value = self.configuration.data.options[currentOption.id]
                    if (option.id === value) {
                        optionElem.addClass("is-active");
                    }

                    optionElem.addClass("show");
                    optionElem.setAttribute("data-value", option.id === value);
                    optionElem.setAttribute("data-parentid", currentOption.id);

                    wrapperElem.appendChild(self.createImageElem(option, optionElem));
                }
            }

            //Name            
            wrapperElem.appendChild(self.createNameElem(option));

            optionElem.appendChild(wrapperElem);
            itemsElem.appendChild(optionElem);

        });

        this.resize();
    }

}

/*
* Entity Helper
**/

export class EntityHelper {

    getEntityBoundingBox = function (entity: Entity, scene: BABYLON.Scene) {
        var entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);
        var entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        var bv = entityBounds.getHierarchyBoundingVectors();
        var bb = new BABYLON.BoundingBox(bv.min, bv.max);
        return bb;
    }

    getEntitySizeDiff = function (entity1: Entity, entity2: Entity, scene: BABYLON.Scene) {

        var entityNode1 = scene.getTransformNodeByUniqueId(entity1.uniqueId);
        var entityBounds1 = entityNode1.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        var bv1 = entityBounds1.getHierarchyBoundingVectors();
        var bb1 = new BABYLON.BoundingBox(bv1.min, bv1.max);

        var entityNode2 = scene.getTransformNodeByUniqueId(entity2.uniqueId);
        var entityBounds2 = entityNode2.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        var bv2 = entityBounds2.getHierarchyBoundingVectors();
        var bb2 = new BABYLON.BoundingBox(bv2.min, bv2.max);

        var diff = bb1.extendSize.subtract(bb2.extendSize);
        return diff;
    }


    getNeighboringEntities = function(module: Module, entity: Entity, scene: BABYLON.Scene) {

        const entityNode = scene.getTransformNodeById(entity.id);
        entityNode.computeWorldMatrix(true);
        let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(entity.rotation._y, entity.rotation._x, entity.rotation._z);
        
        const availableEntities = module.entities.filter(function (e: Entity) {            
            return productLibrary.getProductCategory(e.category) === "UPHOLSTRY" && e.id !== entity.id && e.connectors.length > 0
        });   
        
        const neighbouringEntities: { entity: Entity, connector: Connector, direction: Direction, isConnected: boolean }[] = []

        for (let connector of entity.connectors) {

            const connectorMesh = entityNode.getChildMeshes(true, x => x.name === connector.id)[0];
            connectorMesh.computeWorldMatrix(true);

            var connection = entity.connections.find(x => x.id === connector.id);
            if (connection) {
                const connectionEntity = module.entities.find(x => x.id === connection.entityId);
                if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                    neighbouringEntities.push({ entity: connectionEntity, connector: connector, direction: connector.direction, isConnected: true});
                    continue;
                }
            }        
             
            for (let availableEntity of availableEntities) {

                const availableEntityNode = scene.getTransformNodeById(availableEntity.id);
                availableEntityNode.computeWorldMatrix(true);
                
                const availableEntityBounds = availableEntityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];
                if (availableEntityBounds.intersectsPoint(connectorMesh.absolutePosition)) {
                    if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                        neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                        continue;
                    }
                    
                } 

                const bv = availableEntityNode.getHierarchyBoundingVectors(true);
                const bb = new BABYLON.BoundingBox(bv.min, bv.max);

                if (bb.intersectsPoint(connectorMesh.absolutePosition) && neighbouringEntities.filter(x => x.entity.id === availableEntity.id).length === 0) {
                    if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                        neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                        continue;
                    }                    
                }

                for (let availableEntityConnectors of availableEntity.connectors) {
                    const availableConnectorMesh = availableEntityNode.getChildMeshes(true, x => x.name === availableEntityConnectors.id)[0];
                    if (BABYLON.Vector3.Distance(connectorMesh.absolutePosition,availableConnectorMesh.absolutePosition) <= 0.01) {
                        if (neighbouringEntities.filter(x => x.direction === connector.direction && x.connector.id === connector.id).length === 0) {
                            neighbouringEntities.push({ entity: availableEntity, connector: connector, direction: connector.direction, isConnected: false});
                            continue;
                        }
                    }
                    
                }
           }              
        }

        return neighbouringEntities;
    }

    getAdjacentEntities = function (module: Module, entity: Entity, scene: BABYLON.Scene) {

        const adjacentEntities: AdjacentEntity[] = [];

        if (entity.connections.length === 0)
            return adjacentEntities;

        const entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);

        if (!entityNode)
            return adjacentEntities;

        entityNode.computeWorldMatrix(true);
        const entityBounding = entityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];
          entityBounding.computeWorldMatrix(true);
        const bv1 = entityBounding.getHierarchyBoundingVectors();
        const bb1 = new BABYLON.BoundingBox(bv1.min, bv1.max);      

        let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(entity.rotation._y, entity.rotation._x, entity.rotation._z);
        
        const filteredEntities = module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY");
        for (let i = 0; i < filteredEntities.length; i++) {

            const filteredEntity = filteredEntities[i];
            const filteredEntityNode = scene.getTransformNodeById(filteredEntity.id);

            if (filteredEntity.connections.length === 0)
                continue;           

            if (!filteredEntityNode)
                continue;

            const filteredEntityBounding = filteredEntityNode.getChildMeshes(false, function (m) { return m.id === "entitybounds" })[0];
            const bv2 = filteredEntityBounding.getHierarchyBoundingVectors();
            const bb2 = new BABYLON.BoundingBox(bv2.min, bv2.max);

            let checkPos = bb1.center;         

            if (entity.id !== filteredEntity.id) {

                const front = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(0, 0, bb1.extendSize.z + 0.2), rotationMatrix)))
                const back = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(0, 0, -bb1.extendSize.z - 0.2), rotationMatrix)))

                let left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, 0), rotationMatrix)))
                if (!left) {
                    left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, bb1.extendSize.z - 0.2), rotationMatrix)))
                }
                if (!left) {
                    left = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bb1.extendSize.x + 0.2, 0, -bb1.extendSize.z + 0.2), rotationMatrix)))
                }

                let right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, 0), rotationMatrix)))
                if (!right) {
                    right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, bb1.extendSize.z - 0.2), rotationMatrix)))
                }
                if (!right) {
                    right = filteredEntityBounding.getBoundingInfo().intersectsPoint(checkPos.add(BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(-bb1.extendSize.x - 0.2, 0, -bb1.extendSize.z + 0.2), rotationMatrix)))
                }

                if (back) {
                    adjacentEntities.push({ direction: "BACK", directionVector: null, entity: filteredEntity })
                }

                if (front) {
                    adjacentEntities.push({ direction: "FRONT", directionVector: null, entity: filteredEntity })
                }

                if (left) {
                    adjacentEntities.push({ direction: "LEFT", directionVector: null, entity: filteredEntity })
                }

                if (right) {
                    adjacentEntities.push({ direction: "RIGHT", directionVector: null, entity: filteredEntity })
                }
            }
        }

        return adjacentEntities;
    }
}